import { CardCarousel } from './CardCarousel';
import { type RaceConfig } from '../api/gameConfig';
import { formatModifierValue } from '../utils/modifierLabels';
import { COLORS } from '../constants/theme';
import { RACE_GRADIENTS } from '../constants/raceGradients';

interface ManualModifier {
  label: string;
  display: string;
  sign: 'positive' | 'negative' | 'neutral';
}

// All race-specific mechanics not exposed in the API modifiers map, rendered as table rows
const RACE_EXTRA_MODIFIERS: Partial<Record<string, ManualModifier[]>> = {
  ELF: [
    { label: 'Pitfalls',  display: 'Immune', sign: 'positive' },
    { label: 'Nightmare', display: 'Immune', sign: 'positive' },
  ],
  DWARF: [
    { label: 'Food storage', display: '8×', sign: 'positive' },
  ],
  HALFLING: [
    { label: 'Thief power (TPA)', display: '+50%',  sign: 'positive' },
    { label: 'Stealth recovery',  display: '+1/day', sign: 'positive' },
  ],
  UNDEAD: [
    { label: 'Food required', display: 'None',   sign: 'positive' },
    { label: 'Plague',        display: 'Immune', sign: 'positive' },
  ],
  AVIAN: [
    { label: 'Attack time',  display: '−25%', sign: 'negative' },
    { label: 'Combat gains', display: '−10%', sign: 'negative' },
  ],
  FAERY: [
    { label: 'Stables', display: 'None', sign: 'negative' },
  ],
};

interface RaceSelectorProps {
  races: RaceConfig[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function RaceCard({ race, isSelected }: { race: RaceConfig; isSelected: boolean }) {
  const apiModifiers = Object.entries(race.modifiers)
    .map(([key, value]) => formatModifierValue(key, value))
    .filter((m): m is NonNullable<typeof m> => m !== null);

  const modifiers = [...apiModifiers, ...(RACE_EXTRA_MODIFIERS[race.id] ?? [])];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Image / gradient */}
      <div style={{
        height: 90,
        background: RACE_GRADIENTS[race.id] ?? RACE_GRADIENTS.HUMAN,
        borderRadius: '6px 6px 0 0',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        <img
          src={`/images/races/${race.id.toLowerCase()}.webp`}
          alt={`${race.name}`}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      {/* Body */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 7, flex: 1 }}>
        {/* Name + selected badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: isSelected ? COLORS.link : COLORS.text.primary, flex: 1 }}>
            {race.name}
          </div>
          {isSelected && (
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 3,
              backgroundColor: COLORS.link, color: COLORS.bg, letterSpacing: '0.05em',
              textTransform: 'uppercase', flexShrink: 0,
            }}>
              ✓
            </span>
          )}
        </div>

        {/* Units table */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 600, color: COLORS.text.tertiary, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
            Units
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', color: COLORS.text.tertiary, fontWeight: 500, paddingBottom: 2, fontSize: 10 }}>Name</th>
                <th style={{ textAlign: 'center', color: COLORS.text.tertiary, fontWeight: 500, paddingBottom: 2, width: 26, fontSize: 10 }}>Off</th>
                <th style={{ textAlign: 'center', color: COLORS.text.tertiary, fontWeight: 500, paddingBottom: 2, width: 26, fontSize: 10 }}>Def</th>
                <th style={{ textAlign: 'right', color: COLORS.text.tertiary, fontWeight: 500, paddingBottom: 2, width: 42, fontSize: 10 }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {[race.units.offenseSpecialist, race.units.defenseSpecialist, race.units.elite].map((u) => (
                <tr key={u.name}>
                  <td style={{ color: COLORS.text.secondary, paddingTop: 1 }}>{u.name}</td>
                  <td style={{ textAlign: 'center', color: COLORS.text.primary, fontVariantNumeric: 'tabular-nums' }}>{u.offense}</td>
                  <td style={{ textAlign: 'center', color: COLORS.text.primary, fontVariantNumeric: 'tabular-nums' }}>{u.defense}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: u.baseCost === 0 ? COLORS.positive : COLORS.text.tertiary }}>
                    {u.baseCost === 0 ? 'Free' : `${u.baseCost}g`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modifiers */}
        {modifiers.length > 0 && (
          <div>
            <div style={{ borderTop: `1px solid ${COLORS.border}`, marginBottom: 6 }} />
            <div style={{ fontSize: 9, fontWeight: 600, color: COLORS.text.tertiary, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              Modifiers
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <tbody>
                {modifiers.map((m) => (
                  <tr key={m.label}>
                    <td style={{ color: COLORS.text.secondary, paddingTop: 1 }}>{m.label}</td>
                    <td style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: m.sign === 'positive' ? COLORS.positive : m.sign === 'negative' ? COLORS.negative : COLORS.text.primary,
                    }}>
                      {m.display}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
}

export function RaceSelector({ races, selectedId, onSelect }: RaceSelectorProps) {
  return (
    <CardCarousel
      items={races}
      selectedId={selectedId}
      onSelect={onSelect}
      getItemId={(r) => r.id}
      ariaLabel="Race"
      renderCard={(race, isSelected) => (
        <RaceCard race={race} isSelected={isSelected} />
      )}
    />
  );
}
